/**
 * Router-based translation loading
 * Automatically loads required translations when navigating to routes
 */

import { loadDomains, getMessages } from './messages.js'

// Map routes to required translation domains
const ROUTE_DOMAINS = {
  // Auth routes
  '/login': ['auth'],
  '/register': ['auth'],
  '/reset-password': ['auth'],
  '/auth/sso': ['auth'],
  '/auth/magic-link': ['auth'],
  // Retroact Connect launch landing. ConnectLaunch.vue awaits loadDomain()
  // itself, but the mapping keeps it consistent with every other auth route and
  // lets the domain start loading during navigation rather than after mount.
  '/launch': ['auth'],
  // The page an email link lands on (verify address, reset password). Without
  // this its copy renders as raw keys in every language.
  '/auth/action': ['auth'],
  
  // Patient management routes
  '/patients': ['validation', 'reports'],
  '/patient/:id': ['validation', 'reports'],
  '/add-patient': ['validation'],

  // Results dashboard (children include the in-app prescription view at
  // /results/:id/prescription, which uses reports.* keys).
  '/results': ['reports'],

  // Reports routes
  '/reports': ['reports'],
  '/prescription': ['reports'],
  '/patient-report': ['reports'],

  // Trends dashboard (data sheets + graphics use reports.* keys)
  '/trends': ['reports'],
  
  // Email routes
  '/email-test': ['emails'],
  
  // Ticket routes (marked for future migration)
  '/tickets': ['tickets'],
  '/ticket/:id': ['tickets'],
  
  // Admin routes
  '/admin': ['navigation', 'validation'],
  '/settings': ['navigation', 'validation']
}

/**
 * Load translations required for a route
 * @param {string} path - Route path
 * @param {string} locale - Current locale
 */
export async function loadRouteTranslations(path, locale) {
  // Find matching route pattern
  let domains = []
  
  for (const [pattern, routeDomains] of Object.entries(ROUTE_DOMAINS)) {
    // Simple pattern matching (can be enhanced with regex for dynamic routes)
    if (path.startsWith(pattern.replace('/:id', ''))) {
      domains = routeDomains
      break
    }
  }
  
  if (domains.length > 0) {
    console.log(`📍 Route ${path} requires: ${domains.join(', ')}`)
    await loadDomains(locale, domains)
  }
}

/**
 * Setup router navigation guard for lazy loading
 * @param {Router} router - Vue Router instance
 * @param {I18n} i18n - Vue I18n instance
 */
export function setupRouterGuard(router, i18n) {
  router.beforeEach(async (to, from, next) => {
    const currentLocale = i18n.global.locale.value

    try {
      await loadRouteTranslations(to.path, currentLocale)
      // loadDomains() only merges into the local messages object; we still have
      // to push it into the i18n instance for the newly-loaded keys to render.
      // Without this, lazy domains stay raw until the background preload fires.
      i18n.global.setLocaleMessage(currentLocale, getMessages()[currentLocale])
      next()
    } catch (error) {
      console.error('Failed to load route translations:', error)
      next() // Continue anyway
    }
  })
}
